import Link from "next/link";
import type { DefaultValues } from "react-hook-form";
import type { StandardContactFormDataT } from "@/app/schemas/zod/contact-form-schema";
import type {
  ContactFormConfigT,
  ContactFormFieldConfigT,
} from "./contact-form-types";

export type StandardContactFormFieldNameT =
  | "company"
  | "salutation"
  | "firstname"
  | "lastname"
  | "email"
  | "phone"
  | "comments"
  | "privacy"
  | "mail"
  | "case"
  | "URL"
  | "source"
  | "dealerdesk";

export const standardContactFormDefaultValues: DefaultValues<StandardContactFormDataT> =
  {
    company: undefined,
    salutation: undefined,
    firstname: "",
    lastname: "",
    email: "",
    phone: "",
    comments: undefined,
    privacy: false,
    mail: "",
    case: "CASE",
    URL: undefined,
    source: "Kontakt Anfrage",
    dealerdesk: "Kontakt Darmstadt",
  };

const primaryFields: ContactFormFieldConfigT<StandardContactFormFieldNameT>[] =
  [
    {
      name: "company",
      type: "text",
      label: "Firma",
      placeholder: "Firma",
    },
    {
      name: "salutation",
      type: "select",
      label: "Anrede",
      placeholder: "Bitte auswählen...",
      options: [
        { value: "Herr", label: "Herr" },
        { value: "Frau", label: "Frau" },
        { value: "Divers", label: "Divers" },
      ],
    },
    {
      name: "firstname",
      type: "name",
      label: "Vorname",
      placeholder: "Vorname",
    },
    {
      name: "lastname",
      type: "name",
      label: "Name",
      placeholder: "Nachname",
    },
    {
      name: "email",
      type: "text",
      inputType: "email",
      label: "E-Mail",
      placeholder: "E-Mail",
    },
    {
      name: "phone",
      type: "phone",
      label: "Telefon",
      placeholder: "+49 (123) 45678902",
    },
  ];

const commentsField: ContactFormFieldConfigT<StandardContactFormFieldNameT> = {
  name: "comments",
  type: "textarea",
  label: "Ihre Nachricht",
  placeholder: "Ihre Nachricht...",
  colSpan: 2,
};

const privacyField: ContactFormFieldConfigT<StandardContactFormFieldNameT> = {
  name: "privacy",
  type: "checkbox",
  colSpan: 2,
  label: (
    <span className="text-sm leading-relaxed whitespace-normal sm:whitespace-nowrap">
      Mit absenden bestätigen Sie unsere{" "}
      <Link href="/datenschutz" className="underline">
        Datenschutzerklärung
      </Link>
      .
    </span>
  ),
};

const honeypotField: ContactFormFieldConfigT<StandardContactFormFieldNameT> = {
  name: "mail",
  type: "honeypot",
};

const caseField: ContactFormFieldConfigT<StandardContactFormFieldNameT> = {
  name: "case",
  type: "select",
  hidden: true,
  options: [
    { value: "CASE", label: "CASE" },
    { value: "OPPORTUNITY", label: "OPPORTUNITY" },
  ],
};

const urlField: ContactFormFieldConfigT<StandardContactFormFieldNameT> = {
  name: "URL",
  type: "text",
  hidden: true,
};

export const standardContactFormConfig: ContactFormConfigT<StandardContactFormFieldNameT> =
  {
    sections: [
      {
        id: "primary",
        layout: "grid",
        columns: 2,
        fields: [...primaryFields, honeypotField, caseField, urlField],
      },
      {
        id: "additional",
        layout: "grid",
        columns: 2,
        fields: [commentsField, privacyField],
      },
    ],
    honeypotFieldName: "mail",
    submitButtonLabel: "Nachricht senden",
    submittingLabel: "Wird gesendet...",
    successToast: {
      title: "Nachricht erfolgreich gesendet",
      description: "Wir werden uns in Kürze bei Ihnen melden.",
    },
    errorToast: {
      title: "Fehler beim Senden",
      description:
        "Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.",
    },
  };
